// Заказ после оформления: статус, фото на согласование, доплата, отзыв после вручения.

import { onSite } from '../lib/web.ts';

/** Куда ещё придёт сообщение, кроме экрана заказа. */
const HERE = onSite ? 'сюда и на почту' : 'сюда и в Telegram';
import { useEffect, useRef, useState } from 'react';
import { api, type OrderView } from '../lib/api.ts';
import { useApp } from '../lib/store.tsx';
import { haptic, openLink } from '../lib/tg.ts';
import { dayWord, rub, slotText } from '../lib/format.ts';
import { ErrorNote, Flower, Icon, PhotoPicker, Spinner, TitleTop, toast } from '../ui/kit.tsx';
import { NotifyToggle, ShareRecipient } from './pay.tsx';

const STEPS: Array<{ id: string; text: string }> = [
  { id: 'paid', text: 'Заказ оплачен' },
  { id: 'recipient', text: 'Получатель выбрал адрес и время' },
  { id: 'photo', text: 'Фото букета' },
  { id: 'courier', text: 'Курьер в пути' },
  { id: 'delivered', text: 'Вручён' },
];
const RANK: Record<string, number> = { draft: 0, awaiting_payment: 0, awaiting_recipient: 1, awaiting_surcharge: 1, paid: 2, assembling: 2, photo_sent: 2.5, approved: 3, courier: 4, not_delivered: 4, delivered: 5, cancelled: -1, unpaid: -1 };

/** Время по Москве из ISO: 14:35 */
const mskTime = (iso: string) => {
  const t = new Date(Date.parse(iso) + 180 * 60_000);
  return `${String(t.getUTCHours()).padStart(2, '0')}:${String(t.getUTCMinutes()).padStart(2, '0')}`;
};

/** Ночь по Москве: с 22:00 до 8:00 флористы не на связи. */
export const isNightMsk = (now = Date.now()) => {
  const h = new Date(now + 180 * 60_000).getUTCHours();
  return h < 8 || h >= 22;
};

/** «Повторить заказ»: тот же букет, размер, адрес и открытка — остаётся выбрать время. */
function useRepeat() {
  const { startFlow, catalog, go } = useApp();
  return (o: OrderView) => {
    const design = o.designId ? catalog.find((x) => x.id === o.designId) ?? null : null;
    const gift = o.kind === 'gift';
    startFlow(gift ? 'gift' : 'self', {
      personId: o.personId,
      personName: o.recipientName,
      occasion: o.occasion ?? (gift ? null : 'self'),
      size: o.size,
      design,
      surprise: o.kind === 'surprise',
      surpriseNotes: o.surpriseNotes ?? '',
      prefill: {
        recipientMode: o.recipientMode ?? 'known',
        address: o.address ? { value: o.address, lat: o.lat ?? null, lon: o.lon ?? null, insideMkad: null } : null,
        extra: o.addressExtra ?? '',
        handover: o.handover ?? 'hand',
        rName: o.recipientName ?? '',
        rPhone: o.recipientPhone ?? '',
        cardText: o.cardText ?? '',
      },
    });
    go({ name: 'checkout' });
  };
}

export function OrderScreen({ orderId }: { orderId: string }) {
  const { go, home } = useApp();
  const [o, setO] = useState<OrderView | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [fixing, setFixing] = useState(false);
  const [fixText, setFixText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const repeat = useRepeat();
  const timer = useRef<number | undefined>(undefined);

  const load = () =>
    api.order(orderId).then((x) => {
      setO(x);
      return x;
    }, (e) => setError(e.message));

  useEffect(() => {
    load();
    timer.current = window.setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [orderId]);

  useEffect(() => {
    if (o?.hasPhoto && !photo) api.orderPhoto(o.publicId).then((p) => setPhoto(`data:${p.type};base64,${p.base64}`), () => {});
  }, [o?.hasPhoto]);

  if (!o) return <main className="screen">{error ? <ErrorNote error={error} /> : <Spinner />}</main>;

  const rank = RANK[o.status] ?? 0;
  const gift = o.kind === 'gift';
  const steps = STEPS.filter((s) => s.id !== 'recipient' || o.recipientMode === 'ask');
  const stepState = (id: string) => {
    const r = { paid: 1, recipient: 2, photo: 3, courier: 4, delivered: 5 }[id]!;
    if (rank >= r || (id === 'paid' && rank >= 1)) return 'done';
    if ((id === 'recipient' && rank === 1) || (id === 'photo' && rank >= 2 && rank < 3) || (id === 'courier' && rank === 3)) return 'now';
    return 'next';
  };

  const title =
    o.status === 'draft' ? 'Флорист считает доставку' :
    o.status === 'awaiting_payment' ? (o.paymentMode === 'manual' && !o.payLink ? 'Проверяем заказ' : 'Осталось оплатить') :
    o.status === 'awaiting_recipient' ? 'Оплачено. Ждём адрес от получателя' :
    o.status === 'awaiting_surcharge' ? (o.redeliveryPending ? 'Оплати повторную доставку' : 'Адрес за МКАД') :
    o.status === 'photo_sent' ? 'Букет готов, посмотри' :
    o.status === 'delivered' ? (gift ? 'Цветы вручены' : 'Букет у тебя') :
    o.status === 'unpaid' ? 'Заказ не оплачен' :
    o.status === 'cancelled' ? 'Заказ отменён' :
    o.status === 'not_delivered' ? 'Не получилось вручить' :
    o.status === 'courier' ? 'Курьер в пути' : 'Выдыхай, заказ принят';

  return (
    <main className="screen" style={{ gap: 16 }}>
      <TitleTop title={title} eyebrow={`Заказ ${o.publicId} · ${dayWord(o.date)}${o.slotFrom !== null ? `, ${slotText(o.slotFrom, o.slotTo)}` : ''}`} onBack={home} />
      <ErrorNote error={error} />

      {o.status === 'draft' && (
        <div className="stack center" style={{ alignItems: 'center', padding: '12px 0' }}>
          <Flower fill="#F7ACD6" size={48} />
          <p className="p">{isNightMsk() ? `Сейчас ночь, поэтому цену доставки пришлём утром, после 8:00, ${HERE}.` : `Пришлём цену доставки в течение часа, ${HERE}.`}{onSite ? ' Страницу можно закрыть.' : ' Приложение можно закрыть.'}</p>
        </div>
      )}

      {(o.status === 'unpaid' || o.status === 'cancelled') && (
        <div className="stack">
          <div className="card stack s6">
            <span style={{ fontWeight: 600 }}>
              {o.status === 'unpaid' ? 'Оплата не пришла вовремя, поэтому заказ отменён.' : o.cancelledBy === 'client' ? 'Заказ отменён по твоей просьбе.' : 'Нам очень жаль, но заказ пришлось отменить.'}
            </span>
            {o.cancelReason && o.status === 'cancelled' && o.cancelledBy !== 'client' && <span className="small">Причина: {o.cancelReason}</span>}
            {o.refundStatus === 'needed' && <span className="small">Деньги вернём в течение 1–3 рабочих дней.</span>}
            {o.refundStatus === 'done' && <span className="small">Деньги вернули. В зависимости от банка они придут за 1–3 дня.</span>}
          </div>
          <button type="button" className="btn" onClick={() => repeat(o)}>
            Повторить заказ
          </button>
          <span className="tiny center">Букет, адрес и открытка сохранятся, останется выбрать время.</span>
        </div>
      )}

      {o.status === 'not_delivered' && !o.redelivery && (
        <div className="note">
          <Icon name="info" size={18} />
          <span>Курьер не смог вручить букет. Букет у нас и стоит в воде. Мы свяжемся с тобой и договоримся, когда привезти ещё раз.</span>
        </div>
      )}
      {o.status === 'not_delivered' && o.redelivery && <Redelivery order={o} onDone={load} />}

      {o.offerText && (
        <div className="status" style={{ alignItems: 'flex-start' }}>
          <Icon name="info" size={20} />
          <span>Флорист предлагает замену: {o.offerText}. {onSite ? 'Подходит?' : 'Ответь в сообщении от бота, подходит ли она.'}</span>
        </div>
      )}
      {o.offerText && onSite && (
        <div className="stack s6">
          <button type="button" className="btn" onClick={async () => setO(await api.offer(o.publicId, true).catch((e) => (setError(e.message), o)))}>
            Замена подходит
          </button>
          <button type="button" className="btn outline" onClick={async () => setO(await api.offer(o.publicId, false).catch((e) => (setError(e.message), o)))}>
            Отменить и вернуть деньги
          </button>
        </div>
      )}

      {o.status === 'awaiting_payment' && o.payDeadline && (
        <div className="note">
          <Icon name="clock" size={18} />
          <span>Оплати до {mskTime(o.payDeadline)}, иначе заказ отменится.</span>
        </div>
      )}

      {o.status === 'awaiting_payment' && o.paymentMode === 'manual' && !o.payLink && (
        <div className="note">
          <Icon name="clock" size={18} />
          <span>{isNightMsk() ? `Сейчас ночь, поэтому ссылку на оплату ${rub(o.total)} пришлём утром, после 8:00, ${HERE}.` : `Проверяем заказ. Ссылку на оплату ${rub(o.total)} пришлём ${HERE}, обычно в течение 15 минут.`}</span>
        </div>
      )}
      {o.status === 'awaiting_payment' && (o.paymentMode !== 'manual' || o.payLink) && (
        <button type="button" className="btn" onClick={() => (o.payLink ? openLink(o.payLink) : go({ name: 'pay', orderId: o.publicId }))}>
          Оплатить {rub(o.total)}
        </button>
      )}

      {o.status === 'awaiting_surcharge' && (
        <div className="stack">
          <div className="card stack s6">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ink-2)' }}>{o.redeliveryPending ? 'Букет и первая доставка' : 'Букет и доставка по Москве'}</span>
              <span style={{ color: 'var(--ok-ink)' }}>оплачены</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between', fontWeight: 700, paddingTop: 8, borderTop: '1px solid #F1EDE0' }}>
              <span>{o.redeliveryPending ? 'Повторная доставка' : 'Доплата за МКАД'}</span>
              <span className="price">{o.surcharge ? rub(o.surcharge) : 'считаем…'}</span>
            </div>
          </div>
          <div className="note">
            <Icon name="clock" size={18} />
            <span>{o.redeliveryPending ? 'Оплати, и привезём букет в выбранное окно.' : 'Оплати, чтобы мы успели к выбранному окну. Получателю о деньгах ничего не скажем.'}</span>
          </div>
          {o.surcharge && o.paymentMode === 'manual' && !o.payLink ? (
            <span className="small center">Ссылку на доплату пришлём {HERE}.</span>
          ) : o.surcharge ? (
            <button type="button" className="btn" onClick={() => (o.payLink ? openLink(o.payLink) : go({ name: 'pay', orderId: o.publicId, kind: 'surcharge' }))}>
              {o.payLink ? 'Доплатить по ссылке' : 'Доплатить через СБП'}
            </button>
          ) : (
            <span className="small center">Флорист посчитает доплату и пришлёт её сюда.</span>
          )}
        </div>
      )}

      {o.status === 'awaiting_recipient' && <ShareRecipient order={o} onChange={setO} />}

      {(o.status === 'photo_sent' || photo) && (
        <div className="card big">
          <div className="art" style={{ height: 300, background: '#FBEFF3' }}>
            {photo ? <img src={photo} alt="Фото готового букета" /> : <Spinner label="Загружаем фото" />}
          </div>
          <div className="stack s6" style={{ padding: '14px 16px 16px', gap: 4 }}>
            <span style={{ fontFamily: 'var(--serif)', fontSize: 21 }}>
              {o.kind === 'surprise' ? 'Сюрприз' : o.kind === 'individual' ? 'Индивидуальный букет' : o.designName}, {o.size}
            </span>
            {o.cardText && <span className="small">Открытка: «{o.cardText}»</span>}
          </div>
        </div>
      )}

      {o.status === 'photo_sent' && !fixing && (
        <div className="stack">
          <button
            type="button"
            className="btn"
            onClick={async () => {
              haptic.ok();
              setO(await api.approve(o.publicId));
            }}
          >
            Нравится, отправляйте
          </button>
          <button type="button" className="btn outline" onClick={() => setFixing(true)}>
            Хочу что-то поправить
          </button>
          <p className="tiny center" style={{ margin: 0 }}>
            Если не ответишь за 30 минут, отправим букет, чтобы успеть ко времени.
          </p>
        </div>
      )}
      {fixing && (
        <div className="card stack">
          <label htmlFor="fix" className="label">
            Что поправить?
          </label>
          <input id="fix" className="input" value={fixText} maxLength={500} onChange={(e) => setFixText(e.target.value)} placeholder="Например: поменьше зелени" />
          <span className="tiny">Флорист поправит и пришлёт новое фото.</span>
          <button
            type="button"
            className="btn sm"
            disabled={fixText.trim().length < 2}
            onClick={async () => {
              try {
                setO(await api.fix(o.publicId, fixText.trim()));
                setFixing(false);
                toast('Передали флористу');
              } catch (e: any) {
                setError(e.message);
              }
            }}
          >
            Отправить флористу
          </button>
        </div>
      )}
      {o.status === 'assembling' && o.fixRequest && <div className="status">Флорист поправляет: «{o.fixRequest}»</div>}

      {o.status === 'delivered' && o.liked === null && (
        <button type="button" className="btn" onClick={() => go({ name: 'delivered', orderId: o.publicId })}>
          {gift ? 'Понравилось?' : 'Как тебе букет?'}
        </button>
      )}

      <div className="card stack s6" style={{ fontSize: 14 }}>
        <span className="row" style={{ gap: 10 }}>
          <Icon name="gift" size={18} color="#6B604A" width={1.8} />
          {o.kind === 'surprise' ? 'Сюрприз от флориста' : o.kind === 'individual' ? 'Индивидуальный букет' : `«${o.designName}»`}, {o.size} · {rub(o.total)}
        </span>
        {o.address && (
          <span className="row" style={{ gap: 10 }}>
            <Icon name="pin" size={18} color="#6B604A" width={1.8} />
            {o.address}
          </span>
        )}
        {o.recipientName && (
          <span className="row" style={{ gap: 10 }}>
            <Icon name="user" size={18} color="#6B604A" width={1.8} />
            Получатель: {o.recipientName}
          </span>
        )}
      </div>

      {rank >= 1 && (
        <ol aria-label="Статус заказа" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
          {steps.map((s) => {
            const st = stepState(s.id);
            return (
              <li key={s.id} className="row" style={{ alignItems: 'flex-start', minHeight: 44, gap: 12 }}>
                <span style={{ width: 22, height: 22, borderRadius: 11, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', ...(st === 'done' ? { background: 'var(--ink)' } : st === 'now' ? { background: 'var(--pink-line)' } : { border: '2px solid var(--line-2)' }) }}>
                  {st === 'done' && <Icon name="check" size={13} color="#FEEB9E" width={3} />}
                </span>
                <span style={{ fontWeight: st === 'now' ? 600 : 400, color: st === 'next' ? 'var(--muted)' : 'var(--ink)' }}>{s.text}</span>
              </li>
            );
          })}
        </ol>
      )}

      {rank >= 1 && rank < 5 && !onSite && <NotifyToggle />}

      <div className="bottom">
        {o.canCancel && !confirmCancel && (
          <button type="button" className="quiet" onClick={() => setConfirmCancel(true)}>
            Отменить заказ
          </button>
        )}
        {o.canCancel && confirmCancel && (
          <div className="card stack s6">
            <span style={{ fontWeight: 600 }}>Отменить заказ?</span>
            <span className="small">{o.status === 'draft' || o.status === 'awaiting_payment' ? 'Оплаты не было, так что ничего не спишется.' : 'Флорист ещё не начал собирать, вернём деньги полностью.'}</span>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn sm"
                style={{ flex: 1 }}
                onClick={async () => {
                  try {
                    setO(await api.cancel(o.publicId));
                    setConfirmCancel(false);
                    haptic.ok();
                  } catch (e: any) {
                    setError(e.message);
                    setConfirmCancel(false);
                  }
                }}
              >
                Да, отменить
              </button>
              <button type="button" className="btn sm outline" style={{ flex: 1 }} onClick={() => setConfirmCancel(false)}>
                Нет
              </button>
            </div>
          </div>
        )}
        <button type="button" className="btn light" onClick={() => go({ name: 'support', orderId: o.publicId })}>
          <Icon name="send" size={18} />
          Написать в поддержку
        </button>
        <button type="button" className="quiet" onClick={home}>
          На главную
        </button>
      </div>
    </main>
  );
}

const MISSES = [
  ['too_bright', 'Слишком ярко'],
  ['too_pale', 'Слишком бледно'],
  ['wrong_flowers', 'Не те цветы'],
  ['too_big', 'Слишком большой'],
  ['too_small', 'Слишком скромный'],
  ['wrong_form', 'Не та форма'],
  ['no_box', 'Без коробки'],
] as const;
const LIKES = [
  ['g:pastel', 'Пастель'],
  ['g:white', 'Белые'],
  ['g:warm', 'Тёплые'],
  ['f:mono', 'Монобукет'],
  ['fl:rose', 'Розы'],
] as const;

export function Delivered({ orderId }: { orderId: string }) {
  const { go, home } = useApp();
  const [o, setO] = useState<OrderView | null>(null);
  const [step, setStep] = useState<'ask' | 'remember' | 'saved' | 'bad' | 'taste' | 'tasteSaved' | 'thanks'>('ask');
  const [misses, setMisses] = useState<string[]>([]);
  const [likes, setLikes] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [claimPhoto, setClaimPhoto] = useState<string | null>(null);
  useEffect(() => {
    api.order(orderId).then(setO, (e) => setError(e.message));
  }, [orderId]);
  useEffect(() => {
    if (o?.hasPhoto && !photo) api.orderPhoto(o.publicId).then((p) => setPhoto(`data:${p.type};base64,${p.base64}`), () => {});
  }, [o?.hasPhoto]);
  if (!o) return <main className="screen">{error ? <ErrorNote error={error} /> : <Spinner />}</main>;
  const gift = o.kind === 'gift';
  const who = o.recipientName ?? 'получатель';
  const send = async (p: Record<string, unknown>, next: typeof step) => {
    try {
      await api.feedback(o.publicId, p);
      setStep(next);
    } catch (e: any) {
      setError(e.message);
    }
  };
  const toggle = (arr: string[], set: (v: string[]) => void, x: string) => set(arr.includes(x) ? arr.filter((y) => y !== x) : [...arr, x]);

  return (
    <main className="screen">
      <TitleTop title={step === 'taste' || step === 'tasteSaved' ? (step === 'taste' ? 'Не во вкусе' : 'Учли') : gift ? 'Цветы вручены' : 'Букет у тебя'} eyebrow={gift ? 'Подарок' : `Заказ ${o.publicId}`} />
      <ErrorNote error={error} />
      {step !== 'taste' && <DeliveredHero photo={photo} title={o.kind === 'surprise' ? 'Сюрприз от флориста' : o.kind === 'individual' ? 'Индивидуальный букет' : `«${o.designName}»`} size={o.size} note={step === 'ask' ? (gift ? (o.handover === 'door' ? 'Оставили у двери' : 'Вручили лично в руки') : 'Поставь в воду, а мы подождём твоего мнения') : null} />}
      {step === 'ask' && (
        <div className="bottom">
          <h2 className="h2">{gift ? 'Как прошло, букет понравился?' : 'Как тебе букет?'}</h2>
          <button type="button" className="btn" onClick={() => setStep('remember')}>
            {gift ? 'Да, понравился' : 'Нравится'}
          </button>
          <button type="button" className="quiet" onClick={() => setStep('bad')}>
            Не очень
          </button>
        </div>
      )}
      {step === 'remember' && (
        <div className="bottom">
          <div className="card stack">
            <span className="label">{gift ? `Запомним, что ${who} любит такое?` : 'Запомним этот вкус?'}</span>
            <span className="small">Сохраним в профиле и в следующий раз сразу предложим букеты в том же стиле.</span>
          </div>
          <button type="button" className="btn" onClick={() => send({ liked: true, remember: true }, 'saved')}>
            Да, запомнить
          </button>
          <button type="button" className="quiet" onClick={() => send({ liked: true, remember: false }, 'thanks')}>
            Не надо
          </button>
        </div>
      )}
      {(step === 'saved' || step === 'thanks') && (
        <div className="bottom">
          <div className="status">
            <Icon name="check" size={20} width={2.6} />
            {step === 'saved' ? 'Запомнили' : 'Рады, что понравилось. Выдыхай!'}
          </div>
          {step === 'saved' && (
            <button type="button" className="btn outline" onClick={() => go({ name: o.personId ? 'person' : 'profile', personId: o.personId! } as any)}>
              Открыть профиль
            </button>
          )}
          <button type="button" className="quiet" onClick={home}>
            На главную
          </button>
        </div>
      )}
      {step === 'bad' && (
        <div className="bottom">
          <button type="button" className="promo" onClick={() => setStep('taste')}>
            <span className="stack s6" style={{ gap: 2, flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{gift ? 'Цветы хорошие, но не во вкусе' : 'Цветы хорошие, но не мой стиль'}</span>
              <span className="tiny" style={{ color: 'var(--pink-ink-2)' }}>
                Уточним вкус, и в следующий раз попадём
              </span>
            </span>
            <Icon name="next" size={18} color="#8A2F5F" />
          </button>
          <label htmlFor="what" className="label" style={{ marginTop: 6 }}>
            Или что-то с качеством и доставкой?
          </label>
          <input id="what" className="input" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} placeholder="Опоздал курьер, помялся цветок…" />
          <span className="tiny">Если с цветами что-то не так, пришли фото в течение 24 часов, и мы заменим букет или вернём деньги.</span>
          <PhotoPicker value={claimPhoto} onChange={setClaimPhoto} />
          <button
            type="button"
            className="btn"
            disabled={text.trim().length < 2 && !claimPhoto}
            onClick={async () => {
              await api.support({ topic: 'quality', text, orderId: o.publicId, photo: claimPhoto ?? undefined }).catch((e) => setError(e.message));
              await send({ liked: false, text }, 'thanks');
            }}
          >
            Отправить
          </button>
        </div>
      )}
      {step === 'taste' && (
        <>
          <p className="p">Жаль, что мимо. Расскажи, что было не так, поправим профиль и в следующий раз попадём.</p>
          <div className="stack">
            <span className="label">Что не подошло</span>
            <div className="wrap">
              {MISSES.map(([id, label]) => (
                <button key={id} type="button" className="chip sm" aria-pressed={misses.includes(id)} onClick={() => toggle(misses, setMisses, id)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="stack">
            <span className="label">
              А что ближе? <span style={{ fontWeight: 400, color: 'var(--muted)' }}>Если знаешь</span>
            </span>
            <div className="wrap">
              {LIKES.map(([id, label]) => (
                <button key={id} type="button" className="chip sm" aria-pressed={likes.includes(id)} onClick={() => toggle(likes, setLikes, id)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="promo" onClick={() => go({ name: 'swipes' })}>
            <span style={{ flex: 1 }}>Пройти свайпы заново</span>
            <Icon name="next" size={18} />
          </button>
          <div className="bottom">
            <button
              type="button"
              className="btn"
              onClick={() =>
                send(
                  {
                    liked: false,
                    misses,
                    like: {
                      gammas: likes.filter((x) => x.startsWith('g:')).map((x) => x.slice(2)),
                      forms: likes.filter((x) => x.startsWith('f:')).map((x) => x.slice(2)),
                      flowers: likes.filter((x) => x.startsWith('fl:')).map((x) => x.slice(3)),
                    },
                  },
                  'tasteSaved',
                )
              }
            >
              {gift ? 'Обновить профиль' : 'Обновить мой вкус'}
            </button>
          </div>
        </>
      )}
      {step === 'tasteSaved' && (
        <div className="bottom">
          <div className="status">
            <Icon name="check" size={20} width={2.6} />
            Профиль обновлён
          </div>
          <p className="p">«{o.designName}» отметили как «мимо», похожие больше не предложим. В следующий раз подберём с поправкой.</p>
          <button type="button" className="quiet" onClick={home}>
            На главную
          </button>
        </div>
      )}
    </main>
  );
}

/** Верх экрана «Букет у тебя»: фото от флориста или тёплая заглушка с цветами. */
function DeliveredHero({ photo, title, size, note }: { photo: string | null; title: string; size: string | null; note: string | null }) {
  return (
    <div className="delivered-hero">
      <div className="art">
        {photo ? (
          <img src={photo} alt="Фото букета" />
        ) : (
          <div className="delivered-bloom" aria-hidden="true">
            <Flower size={120} fill="#F7ACD6" center="#FAFBF6" />
            <Flower size={84} fill="#FEEB9E" center="#FAFBF6" />
            <Flower size={60} fill="#FFFFFF" center="#FEEB9E" />
          </div>
        )}
        <span className="tag">
          <Icon name="check" size={12} width={3} /> Вручён
        </span>
      </div>
      <div className="stack s6" style={{ padding: '14px 18px 18px', gap: 4 }}>
        <span style={{ fontFamily: 'var(--serif)', fontSize: 22 }}>
          {title}
          {size ? `, ${size}` : ''}
        </span>
        {note && <span className="small">{note}</span>}
      </div>
    </div>
  );
}

/** Не вручили: выбрать день и окно, когда привезти букет ещё раз. */
function Redelivery({ order, onDone }: { order: OrderView; onDone: () => unknown }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.redeliverySlots>> | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.redeliverySlots(order.publicId, date ?? undefined).then(
      (r) => {
        setData(r);
        setSlot(r.slots.find((s) => s.nearest && s.available)?.from ?? r.slots.find((s) => s.available)?.from ?? null);
      },
      (e) => setError(e.message),
    );
  }, [order.publicId, date]);
  if (!data) return error ? <ErrorNote error={error} /> : <Spinner />;
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(data.today + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
  const cur = data.date;
  return (
    <div className="stack">
      <div className="note">
        <Icon name="info" size={18} />
        <span>
          Не получилось вручить букет{data.reason ? ` (${data.reason.toLowerCase()})` : ''}. Он ждёт в мастерской и стоит в воде. Выбери, когда привезти ещё раз
          {data.price ? `, повторная доставка ${rub(data.price)}` : ', повторная доставка за наш счёт'}.
        </span>
      </div>
      <span className="label">День</span>
      <div className="wrap">
        {days.map((d) => (
          <button key={d} type="button" className="chip pink" aria-pressed={cur === d} onClick={() => setDate(d)}>
            {dayWord(d, data.today)}
          </button>
        ))}
      </div>
      <span className="label">Время</span>
      <div className="wrap">
        {data.slots.length === 0 && <span className="small">В этот день окон уже нет, выбери другой.</span>}
        {data.slots.map((s) => (
          <button key={s.from} type="button" className="chip pink" disabled={!s.available} aria-pressed={slot === s.from} onClick={() => setSlot(s.from)} style={s.available ? undefined : { opacity: 0.4 }}>
            {slotText(s.from, s.to)}
            {s.full ? ' · занято' : ''}
          </button>
        ))}
      </div>
      <ErrorNote error={error} />
      <button
        type="button"
        className="btn"
        disabled={busy || slot === null}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await api.redelivery(order.publicId, { date: cur, slotFrom: slot! });
            await onDone();
          } catch (e: any) {
            setError(e.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {data.price ? `Выбрать и оплатить ${rub(data.price)}` : 'Привезти в это время'}
      </button>
    </div>
  );
}
